import Seo from './Seo'

/* React */
import { useState, useEffect, useMemo, useRef, useId } from 'react'
import parse from 'html-react-parser'

/* Material */
import Container from '@mui/material/Container'
import Typo from '@mui/material/Typography'
import useStyles from './Filter.styles'

/* Redux */
import { useDispatch, useSelector } from 'src/redux/hooks'
import magazineUrl from 'src/functions/magazineUrl'
import config from 'src/config'
import { seoDescription, seoText } from 'src/functions/seo'

/* Types */
import { tProductMagazineReference, tProductPreview } from 'src/shared/frontendTypes/product'
import { MenuPageBuilder } from 'src/shared/dbTypes/menuPageBuilder'
import { tBrands } from 'src/shared/frontendTypes/brand'
import type { BreadcrumbItem } from 'src/shared/frontendTypes/product'
import type { item as NavigationItem } from 'src/shared/frontendTypes/navigation'

/* Components */
import Brands from './Components/Brands/Brands'
import Products from './Components/Products/Products'
import FilterTopBar from './Components/FilterTopBar/FilterTopBar'
import FilterStructuredData from './StructuredData'
import { BreadcrumbTrail, SeoBreadcrumbJsonLd } from 'src/components/Breadcrumbs/Breadcrumbs'
import FilterTreeAccordion from 'src/layout/Navigation/FilterTree/FilterTreeAccordion'
import { resolvePageBuilder } from 'src/pageBuilder/config'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import {
  createLandingPageImageAiGeneratedMatcher,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'

const sanitizeRichTextHtml = (value?: string) =>
  String(value || '')
    .replace(/<p(?:\s[^>]*)?>(?:\s|&nbsp;|\u00a0|<br\s*\/?>)*<\/p>/gi, '')
    .replace(/<div(?:\s[^>]*)?>(?:\s|&nbsp;|\u00a0|<br\s*\/?>)*<\/div>/gi, '')
    .trim()

const extractFirstHeadingHtml = (value: string) => {
  const match = value.match(/<h([1-3])(?:\s[^>]*)?>([\s\S]*?)<\/h\1>/i)

  if (!match || match.index === undefined) {
    return {
      headingHtml: '',
      bodyHtml: value,
    }
  }

  return {
    headingHtml: match[2].trim(),
    bodyHtml: `${value.slice(0, match.index)}${value.slice(match.index + match[0].length)}`.trim(),
  }
}

const normalizeDisplayText = (value?: string) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()

const safeParseHtml = (value?: string) => {
  const sanitizedValue = sanitizeRichTextHtml(value)
  if (!sanitizedValue) return null

  try {
    return parse(sanitizedValue, { trim: true })
  } catch (error) {
    console.error('Konnte Filter-Beschreibung nicht rendern.', error)
    return sanitizedValue
  }
}

const normalizeMagazineImageUrl = (value?: string) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('/api/media/landingpages/')) return trimmed.replace('/api/media/landingpages/', '/api/images/landingpages/')
  if (trimmed.startsWith('/media/landingpages/')) return trimmed.replace('/media/landingpages/', '/api/images/landingpages/')
  if (trimmed.startsWith('/images/landingpages/')) return trimmed.replace('/images/landingpages/', '/api/images/landingpages/')
  return trimmed
}

const openMagazineReference = (magazine: tProductMagazineReference) => {
  if (typeof window === 'undefined') return

  const targetHash = `#magazin:${magazine.id}:${magazine.pageIndex || 0}`
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
  window.setTimeout(() => {
    window.location.hash = targetHash
  }, 0)
}

const getMagazineFaqItems = (magazines: tProductMagazineReference[]) => {
  const seen = new Set<string>()

  return magazines
    .flatMap(magazine => (magazine.faqItems || []).map(item => ({
      ...item,
      magazine,
    })))
    .filter(item => {
      const question = item.question.trim()
      const answer = item.answer.trim()
      const key = `${question.toLocaleLowerCase()}::${answer.toLocaleLowerCase()}`
      if (!question || !answer || seen.has(key)) return false
      seen.add(key)
      return true
    })
}

const renderFaqAnswer = (answer: string) =>
  answer
    .split(/\r?\n/)
    .map(part => part.trim())
    .filter(Boolean)
    .map((part, index) => <p key={index}>{part}</p>)

const ContextMagazineFaqs: React.FC<{ magazines: tProductMagazineReference[] }> = ({ magazines }) => {
  const classes = useStyles()
  const items = getMagazineFaqItems(magazines)
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }

  if (items.length === 0) return null

  return <section className={classes.magazineFaqSection}>
    <script
      type='application/ld+json'
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c') }}
    />
    <div className={classes.magazineFaqHeader}>
      <span>Ratgeber & FAQ</span>
      <h2>Pflege, Beratung und häufige Fragen</h2>
    </div>
    <div className={classes.magazineFaqGrid}>
      {items.map(item => {
        const href = item.magazine.href || magazineUrl(item.magazine.id, item.magazine.slug, item.magazine.pageIndex)

        return <article key={`${item.magazine.id}-${item.question}`} className={classes.magazineFaqItem}>
          {item.topic && <small>{item.topic}</small>}
          <h3>{item.question}</h3>
          <div>{renderFaqAnswer(item.answer)}</div>
          <a
            href={href}
            onClick={event => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
              event.preventDefault()
              openMagazineReference(item.magazine)
            }}
          >
            Weiter im Ratgeber
          </a>
        </article>
      })}
    </div>
  </section>
}

interface Props {
  pageType?: 'menu' | 'brand' | 'brandMenu'
  products: tProductPreview[]
  page: number
  size: number
  count?: number
  loading?: boolean
  error?: string
  searchSuggestions?: string[]
  searchAdjustment?: import('src/shared/searchRules').SearchAdjustment
  brands?: tBrands
  title?: string
  description?: string // Kann vom Hersteller ODER vom Menü sein
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  canonicalUrl?: string  // Fertig gebaute URL für den canonical Link
  noindex?: boolean
  brandLogo?: string
  requireStock?: boolean
  breadcrumb?: BreadcrumbItem[]
  subcategoryItems?: NavigationItem[]
  subcategoryParentBrand?: NavigationItem | null
  showCatalogFilters?: boolean
  sitePageBuilder?: MenuPageBuilder
  magazines?: tProductMagazineReference[]
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const Filter: React.FC<Props> = ({ 
  pageType,
  products, page, size, count, loading, error, searchSuggestions, searchAdjustment,
  brands,
  title, description,
  metaTitle, metaDescription, metaKeywords,
  canonicalUrl,
  noindex,
  brandLogo,
  requireStock,
  breadcrumb,
  subcategoryItems,
  subcategoryParentBrand,
  showCatalogFilters,
  sitePageBuilder,
  magazines,
  landingPageImageAiGenerated,
}) => {
  const classes = useStyles()
  const isAiGeneratedLandingImage = useMemo(
    () => createLandingPageImageAiGeneratedMatcher(landingPageImageAiGenerated),
    [landingPageImageAiGenerated]
  )
  const dispatch = useDispatch()
  const filterTree = useSelector(store => store.Filter.filterTree)
  const filterBrands = useSelector(store => store.Filter.filterBrands)
  const hasFilterControls = filterBrands.length > 0
    || filterTree.some(filter => filter.options.length > 0)
  const showFilterRegion = Boolean(
    showCatalogFilters
    && ((count || 0) > 0 || products.length > 0 || hasFilterControls)
  )
  const openFilterDrawer = () => {
    dispatch({ type: 'navigationFilterToggle', payload: true })
    dispatch({ type: 'navigationDrawerToggle', payload: true })
  }

  // Eine einzige Ref für das "Weiterlesen"-Feature
  const descRef = useRef<HTMLDivElement>(null)
  const descriptionId = useId()
  const resultsRef = useRef<HTMLDivElement>(null)

  const [isTextOverflown, setIsTextOverflown] = useState(false)
  const [readMore, toggleReadMore] = useState(false)
  const [filterMaxHeight, setFilterMaxHeight] = useState<number | undefined>(undefined)

  const seoTitle = metaTitle && metaTitle.trim() !== "" ? metaTitle : (title || "Suche");
  const brandLogoSrc = resolveBrandLogoSrc(brandLogo)
  const cleanDescription = sanitizeRichTextHtml(description)
  const descriptionParts = extractFirstHeadingHtml(cleanDescription)
  const introHeadingHtml = brandLogoSrc
    ? descriptionParts.headingHtml || normalizeDisplayText(metaTitle)
    : ''
  const descriptionBody = introHeadingHtml ? descriptionParts.bodyHtml : cleanDescription
  const parsedDescription = safeParseHtml(descriptionBody.replace(/<(\/?)h1\b/gi, '<$1h2'))
  const magazineReferences = magazines || []
  const faqMagazines = magazineReferences.filter(magazine =>
    (magazine.mode || 'magazine') === 'faq' && (magazine.faqItems || []).length > 0
  )
  const editorialMagazines = magazineReferences.filter(magazine => (magazine.mode || 'magazine') !== 'faq')
  const builderContentWidth = showCatalogFilters
    ? resolvePageBuilder(sitePageBuilder).layout.contentWidth
    : 'contained'
  const containerMaxWidth = builderContentWidth === 'full'
    ? false
    : builderContentWidth === 'wide'
      ? 'xl'
      : 'lg'

  useEffect(() => {
    const updateDescriptionOverflow = () => {
      if (descRef.current) {
        setIsTextOverflown(isOverflown(descRef.current))
      }
    }

    updateDescriptionOverflow()
    const timeout = typeof window !== 'undefined'
      ? window.setTimeout(updateDescriptionOverflow, 0)
      : undefined

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateDescriptionOverflow)
    }

    return () => {
      if (timeout !== undefined) window.clearTimeout(timeout)
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateDescriptionOverflow)
      }
    }
  }, [description])

  useEffect(() => {
    const resultsNode = resultsRef.current
    if (!resultsNode || typeof window === 'undefined') return

    const updateFilterHeight = () => {
      setFilterMaxHeight(Math.max(260, Math.round(resultsNode.getBoundingClientRect().height)))
    }

    updateFilterHeight()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateFilterHeight)
      return () => window.removeEventListener('resize', updateFilterHeight)
    }

    const observer = new ResizeObserver(updateFilterHeight)
    observer.observe(resultsNode)
    window.addEventListener('resize', updateFilterHeight)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateFilterHeight)
    }
  }, [products, brands, count, page, size, showFilterRegion])

  return (
    <Container maxWidth={containerMaxWidth} className={classes.container}>
      
      <Seo 
        title={seoTitle}
        description={seoDescription(metaDescription?.trim() || description, `${seoText(title || seoTitle)} online entdecken. Passende Produkte, Größen und Varianten im Sortiment vergleichen.`)}
        keywords={metaKeywords && metaKeywords.trim() !== "" ? metaKeywords : undefined}
        canonical={canonicalUrl}
        noindex={noindex}
      />
      <SeoBreadcrumbJsonLd items={breadcrumb} />
      <FilterStructuredData
        pageType={pageType}
        title={title}
        description={description}
        metaTitle={metaTitle}
        metaDescription={metaDescription}
        canonicalUrl={canonicalUrl}
        brandLogoSrc={brandLogoSrc}
        products={products}
        page={page}
        size={size}
        brands={brands}
        magazines={magazineReferences}
      />
      <BreadcrumbTrail items={breadcrumb} />

      <div className={classes.titleBlock}>
        {brandLogoSrc && (
          <span className={classes.brandLogoWrap}>
            <img
              src={brandLogoSrc}
              alt={title ? `Logo von ${title}` : 'Herstellerlogo'}
              className={classes.brandLogo}
            />
            <AiGeneratedBadge
              aiGenerated={isAiGeneratedLandingImage(brandLogoSrc)}
              size='compact'
              placement='top-left'
            />
          </span>
        )}
        {introHeadingHtml ? (
          <Typo
            component='h1'
            variant='h4'
            color='inherit'
            gutterBottom
            className={classes.seoIntroTitle}
          >
            {descriptionParts.headingHtml ? parse(descriptionParts.headingHtml) : introHeadingHtml}
          </Typo>
        ) : (
          <Typo component='h1' variant='h4' color='inherit' gutterBottom>
            {title || "Suche"}
          </Typo>
        )}
      </div>

      {parsedDescription && (
        <>
          <Typo 
            ref={descRef}
            id={descriptionId}
            component='div' 
            variant='body1' 
            color='inherit' 
            gutterBottom
            sx={{
              maxHeight: readMore ? 'auto' : '200px',
              overflowY: 'hidden',
              overflowX: 'hidden',
            }}
          >
            {parsedDescription}
          </Typo>
          {!readMore && isTextOverflown && (
            <Typo
              component='button'
              type='button'
              variant='button'
              aria-expanded={false}
              aria-controls={descriptionId}
              color='primary.main'
              sx={{ cursor: 'pointer', border: 0, padding: 0, backgroundColor: 'transparent' }}
              onClick={() => toggleReadMore(true)}
            >
              weiter lesen
            </Typo>
          )}
          {readMore && isTextOverflown && (
            <Typo
              component='button'
              type='button'
              variant='button'
              aria-expanded={true}
              aria-controls={descriptionId}
              color='primary.main'
              sx={{ cursor: 'pointer', marginLeft: '1rem', border: 0, padding: 0, backgroundColor: 'transparent' }}
              onClick={() => toggleReadMore(false)}
            >
              weniger lesen
            </Typo>
          )}
        </>
      )}

      <FilterTopBar
        subcategoryItems={showCatalogFilters ? subcategoryItems : []}
        subcategoryParentBrand={subcategoryParentBrand}
        count={showCatalogFilters ? count : undefined}
        showFilterButton={showFilterRegion}
        onFilterClick={openFilterDrawer}
      />

      {faqMagazines.length > 0 && <ContextMagazineFaqs magazines={faqMagazines} />}

      {editorialMagazines.length > 0 && (
        <section className={classes.magazineFeature}>
          <div className={classes.magazineFeatureHeader}>
            <span>Aus dem Magazin</span>
            <h2>Stories, Ideen und Berichte</h2>
          </div>
          <div className={classes.magazineFeatureGrid}>
            {editorialMagazines.map(magazine => {
              const href = magazine.href || magazineUrl(magazine.id, magazine.slug, magazine.pageIndex)

              return <a
                key={magazine.id}
                className={classes.magazineFeatureCard}
                href={href}
                onClick={event => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
                  event.preventDefault()
                  openMagazineReference(magazine)
                }}
              >
                <span className={classes.magazineFeatureCover}>
                  {magazine.coverImage?.src
                    ? <img src={normalizeMagazineImageUrl(magazine.coverImage.src)} alt={magazine.coverImage.alt || magazine.title} />
                    : <span>{magazine.title}</span>
                  }
                  <AiGeneratedBadge
                    aiGenerated={magazine.coverImage?.aiGenerated || isAiGeneratedLandingImage(magazine.coverImage?.src)}
                    size='compact'
                    placement='top-left'
                  />
                </span>
                <span className={classes.magazineFeatureText}>
                  {magazine.issue && <small>{magazine.issue}</small>}
                  <strong>{magazine.title}</strong>
                  {magazine.subtitle && <span>{magazine.subtitle}</span>}
                </span>
              </a>
            })}
          </div>
        </section>
      )}

      <div className={[
        classes.listingShell,
        showFilterRegion ? classes.listingShellWithSidebar : '',
      ].join(' ')}>
        {showFilterRegion &&
          <aside className={classes.filterSidebar} aria-label='Produktfilter'>
            <div
              className={classes.filterSidebarInner}
              style={filterMaxHeight ? { maxHeight: filterMaxHeight } : undefined}
            >
              <Typo component='h2' className={classes.filterSidebarTitle}>
                Filtern
              </Typo>
              {hasFilterControls &&
                <FilterTreeAccordion />
              }
            </div>
          </aside>
        }

        <div className={classes.results} ref={resultsRef}>
          <hr className={classes.startHr} />

          {brands && brands.length > 0 && (
            <>
              <Brands {...{ brands }} />
              <hr className={classes.startHr} />
            </>
          )}

          <Products
            {...{ products, page, size, count, requireStock, loading, error, searchSuggestions, searchAdjustment }}
            catalogLayout={showCatalogFilters}
          />
        </div>
      </div>
    </Container>
  )
}

export default Filter

const isOverflown = ({
  clientWidth,
  clientHeight,
  scrollWidth,
  scrollHeight,
}: HTMLDivElement) =>
  scrollHeight > clientHeight || scrollWidth > clientWidth

const resolveBrandLogoSrc = (src?: string) => {
  const trimmed = String(src || '').trim()
  if (!trimmed) return undefined
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
    return trimmed
  }
  return `${config.image_server}/brand/${trimmed}`
}
